import { useState, useEffect } from "react";
import { X } from "lucide-react";
import InstallInstructionsContent from "./InstallInstructionsContent";

/**
 * Auto-popup shown a few seconds after first visit nudging the user to install
 * Lala Land as a PWA. Now covers all three platforms: iPhone, Android, and
 * desktop (Chrome / Edge / Brave) — instead of mobile-only.
 *
 * Dismissal is sticky for 7 days via localStorage.
 */
const InstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [defaultTab, setDefaultTab] = useState("ios");
  const [isStandalone, setIsStandalone] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    setIsStandalone(standalone);

    // Check if dismissed before
    const dismissed = localStorage.getItem("installPromptDismissed");
    const dismissedTime = dismissed ? parseInt(dismissed, 10) : 0;
    const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);

    // Detect device → which tab to open first. Mobile-only: we don't show
    // the prompt to desktop visitors (Lauren said she's not worried about
    // desktop install).
    const ua = window.navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(ua);
    const isPixel = /pixel/.test(ua);
    const isAndroid = /android/.test(ua);
    const isMobile = isIOS || isAndroid;

    if (isIOS) setDefaultTab("ios");
    else if (isPixel) setDefaultTab("google");
    else if (isAndroid) setDefaultTab("android");
    else setDefaultTab("ios");

    // Only nudge mobile users to install — desktop visitors are skipped.
    if (isMobile && !standalone && daysSinceDismissed > 7) {
      const timer = setTimeout(() => setShowPrompt(true), 3500);
      return () => clearTimeout(timer);
    }
  }, []);

  // Capture beforeinstallprompt for one-tap native install on Chrome / Android
  useEffect(() => {
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handleBeforeInstall);
    return () => window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
  }, []);

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem("installPromptDismissed", Date.now().toString());
  };

  const handleInstalled = () => {
    setShowPrompt(false);
    setDeferredPrompt(null);
  };

  if (!showPrompt || isStandalone) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 animate-fade-in"
        onClick={handleDismiss}
        data-testid="install-prompt-backdrop"
      />

      {/* Modal — bottom-sheet on mobile, centered card on desktop.
         Wrapper has pointer-events:none so clicks fall through to the
         backdrop (which closes the modal); inner card re-enables pointer
         events. This is the only reliable way to make backdrop click-to-
         close work on mobile when the wrapper expands to nearly full screen. */}
      <div
        className="fixed inset-x-0 bottom-0 sm:inset-0 sm:flex sm:items-center sm:justify-center z-50 p-4 pb-20 sm:pb-4 animate-slide-up pointer-events-none"
        data-testid="install-prompt-modal"
      >
        <div
          className="bg-background border border-primary/20 rounded-2xl shadow-2xl max-w-md mx-auto overflow-hidden w-full pointer-events-auto"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 p-4 border-b border-primary/10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img
                  src="/logo192.png"
                  alt="Lala Land"
                  className="w-12 h-12 rounded-xl shadow-lg object-cover"
                />
                <div>
                  <h3 className="font-bold text-primary text-lg">Install Lala Land</h3>
                  <p className="text-xs text-muted-foreground">Add to your home screen</p>
                </div>
              </div>
              <button
                onClick={handleDismiss}
                className="p-2 hover:bg-primary/10 rounded-full transition-colors"
                aria-label="Close"
                data-testid="install-prompt-close"
              >
                <X className="h-5 w-5 text-muted-foreground" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-4">
            <p className="text-sm text-muted-foreground mb-4 text-center">
              Whether you're on iPhone, Android, or a Google Pixel — install Lala Land for the full sacred experience. 🌳
            </p>

            <InstallInstructionsContent
              defaultTab={defaultTab}
              deferredPrompt={deferredPrompt}
              onInstalled={handleInstalled}
            />
          </div>

          {/* Footer */}
          <div className="p-4 pt-2 pb-6">
            <button
              onClick={handleDismiss}
              className="w-full py-3 text-sm font-medium text-muted-foreground hover:text-primary transition-colors bg-secondary/30 rounded-xl"
              data-testid="install-prompt-maybe-later"
            >
              Maybe later
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default InstallPrompt;
